import React, { useCallback, useEffect, useRef, useState } from "react";

import ReactModal from "react-modal";
import styled from "styled-components";

import { registerDialogHandlers, resetDialogHandlers } from "shared/ui/feedback/dialog";

const AlertModal = styled(ReactModal)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  width: 424px;
  min-height: 160px;
  border-radius: 20px;
  background-color: #fff;
  padding: 25px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
`;

const AlertTitle = styled.div`
  width: 100%;
  font-family: "NotoSansKRBold", "Noto Sans KR", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.85);
  text-align: left;
  margin-bottom: 10px;
`;

const AlertMessage = styled.div`
  width: 100%;
  font-family: "Noto Sans KR", sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.85);
  white-space: pre-line;
  text-align: left;
`;

const ButtonBar = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 20px;
  margin-top: auto;
  padding-top: 20px;
`;

const ConfirmButton = styled.button`
  border: none;
  background-color: transparent;
  font-family: "Noto Sans KR", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #000;
  cursor: pointer;
`;

export function GlobalAlertProvider({ children }) {
  const originalAlertRef = useRef(window.alert);
  const originalConfirmRef = useRef(window.confirm);
  const queueRef = useRef([]);
  const confirmQueueRef = useRef([]);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [currentConfirm, setCurrentConfirm] = useState(null);

  useEffect(() => {
    const originalAlert = originalAlertRef.current;
    const originalConfirm = originalConfirmRef.current;

    ReactModal.setAppElement("#root");

    const openAlert = (message, options = {}) => {
      queueRef.current.push({
        message: String(message ?? ""),
        title: options.title,
      });
      setCurrentAlert((prev) => prev ?? queueRef.current.shift());
    };

    const openConfirm = (message, options = {}) =>
      new Promise((resolve) => {
        confirmQueueRef.current.push({
          message: String(message ?? ""),
          title: options.title,
          confirmLabel: options.confirmLabel,
          cancelLabel: options.cancelLabel,
          resolve,
        });
        setCurrentConfirm((prev) => prev ?? confirmQueueRef.current.shift());
      });

    registerDialogHandlers({
      alertHandler: openAlert,
      confirmHandler: openConfirm,
    });

    window.alert = openAlert;
    return () => {
      resetDialogHandlers();
      window.alert = originalAlert;
      window.confirm = originalConfirm;
    };
  }, []);

  const handleClose = useCallback(() => {
    if (queueRef.current.length > 0) {
      setCurrentAlert(queueRef.current.shift());
      return;
    }

    setCurrentAlert(null);
  }, []);

  const handleConfirmClose = useCallback(
    (result) => {
      if (currentConfirm) {
        currentConfirm.resolve(result);
      }

      if (confirmQueueRef.current.length > 0) {
        setCurrentConfirm(confirmQueueRef.current.shift());
        return;
      }

      setCurrentConfirm(null);
    },
    [currentConfirm],
  );

  // 열린 모달에서 Enter를 누르면 확인(알림=닫기, 컨펌=승인)으로 처리한다.
  // 오버레이 클릭·ESC는 react-modal의 onRequestClose로 처리된다.
  useEffect(() => {
    if (currentAlert === null && currentConfirm === null) return undefined;
    const handleEnterKey = (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      if (currentConfirm !== null) {
        handleConfirmClose(true);
      } else {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [currentAlert, currentConfirm, handleClose, handleConfirmClose]);

  return (
    <>
      {children}
      <AlertModal
        isOpen={currentAlert !== null}
        onRequestClose={handleClose}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.28)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "30px",
            zIndex: 2000,
          },
          content: {
            position: "static",
            inset: "auto",
            border: "none",
            overflow: "visible",
            background: "#fff",
            padding: "25px",
            borderRadius: "20px",
          },
        }}
      >
        <AlertTitle>{currentAlert?.title ?? "알림"}</AlertTitle>
        <AlertMessage>{currentAlert?.message ?? ""}</AlertMessage>
        <ButtonBar>
          <ConfirmButton onClick={handleClose}>확인</ConfirmButton>
        </ButtonBar>
      </AlertModal>
      <AlertModal
        isOpen={currentConfirm !== null}
        onRequestClose={() => handleConfirmClose(false)}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "30px",
            zIndex: 2000,
          },
          content: {
            position: "static",
            inset: "auto",
            border: "none",
            overflow: "visible",
            background: "#fff",
            padding: "25px",
            borderRadius: "20px",
          },
        }}
      >
        <AlertTitle>{currentConfirm?.title ?? "확인"}</AlertTitle>
        <AlertMessage>{currentConfirm?.message ?? ""}</AlertMessage>
        <ButtonBar>
          <ConfirmButton onClick={() => handleConfirmClose(false)}>
            {currentConfirm?.cancelLabel ?? "취소"}
          </ConfirmButton>
          <ConfirmButton onClick={() => handleConfirmClose(true)}>
            {currentConfirm?.confirmLabel ?? "확인"}
          </ConfirmButton>
        </ButtonBar>
      </AlertModal>
    </>
  );
}
